<template>
  <full-page-modal @cancel="onCancel">
    <div class="flex-center h-100">
      <b-form class="form" @submit.prevent="onSubmit" @reset="onCancel">
        <h2 class="mb-5 text-center">Your Profile</h2>
        <div class="row mb-5 flex-center flex-column">
          <avatar-input
            :photo-url="getPhotoUrl(userData.photoId)"
            ref="avatarInput"
            length="100"
            @change="saveProfileImage"
          ></avatar-input>
          <div class="field-error-message" v-if="errors.profileImage">
            {{ errors.profileImage }}
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-6">
            <label class="ml-2 pl-1 mb-1" for="first-name">First name</label>
            <b-form-input
              v-model="userData.firstName"
              :state="errors.firstName ? false : null"
              id="first-name"
              v-no-capture
            ></b-form-input>
            <b-form-invalid-feedback :state="errors.firstName">
              {{ errors.firstName && errors.firstName[0] }}
            </b-form-invalid-feedback>
          </div>
          <div class="col-6">
            <label class="ml-2 pl-1 mb-1" for="last-name">Last name</label>
            <b-form-input
              v-model="userData.lastName"
              :state="errors.lastName ? false : null"
              id="last-name"
              v-no-capture
            ></b-form-input>
            <b-form-invalid-feedback :state="errors.lastName">
              {{ errors.lastName && errors.lastName[0] }}
            </b-form-invalid-feedback>
          </div>
        </div>
        <div class="row mb-5">
          <div class="col-12">
            <label class="ml-2 pl-1 mb-1" for="email">Email</label>
            <b-form-input
              type="email"
              :value="userData.email"
              id="email"
              disabled
              v-no-capture
            ></b-form-input>
          </div>
        </div>
        <div class="form-error text-medium" v-if="errors.formError">
          {{ errors.formError }}
        </div>
        <submit-buttons
          :is-loading="isSubmitting"
          class="mt-3 ml-2 pl-1"
          @save="onSubmit"
          @cancel="onCancel"
        />
      </b-form>
    </div>
  </full-page-modal>
</template>

<script>
import FullPageModal from '@/components/FullPageModal.vue'
import * as R from 'ramda'
import { BForm, BFormInput, BFormInvalidFeedback } from 'bootstrap-vue'
import SubmitButtons from '@/components/SubmitButtons'
import getPhotoUrl from '@/utils/getPhotoUrl'
import { DEFAULT_ERROR } from '@/constants/messages'
/*
This component can be used to update user profile. It allows editing of
 - First Name
 - Last Name
 - Profile Image

 @emit
 - hide
 */
export default {
  name: 'UpdateProfile',
  data() {
    return {
      userData: {},
      errors: {},
      isSubmitting: false
    }
  },
  components: {
    FullPageModal,
    BForm,
    BFormInput,
    BFormInvalidFeedback,
    SubmitButtons,
    AvatarInput: () => import(/* webpackChunkName: "avatar-input" */ '@/components/AvatarInput.vue')
  },
  computed: {
    user() {
      return this.$store.getters.user
    }
  },
  methods: {
    onSubmit() {
      this.isSubmitting = true
      this.saveName()
        .then(() => {
          this.onCancel()
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },
    onCancel() {
      this.$emit('hide')
    },
    async saveProfileImage({ blob }) {
      try {
        await this.$store.dispatch('uploadProfilePic', { blob: blob })
        this.errors = R.dissoc('profileImage', this.errors)
      } catch (e) {
        this.userData.photoId = this.user.photoId
        this.errors = {
          ...this.errors,
          profileImage: R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        }
      }
    },
    async saveName() {
      const { firstName, lastName } = this.userData
      try {
        if (firstName === this.user.firstName && lastName === this.user.lastName) return
        await this.$store.dispatch('updateProfile', { firstName, lastName })
        this.errors = R.omit(['firstName', 'lastName', 'formError'], this.errors)
      } catch (e) {
        const errors = R.path(['response', 'data', 'detail'], e)
        if (errors) {
          this.errors = { ...this.errors, ...errors }
        } else {
          this.errors = {
            ...this.errors,
            formError: R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
          }
        }
        throw new Error()
      }
    },
    getPhotoUrl
  },
  mounted() {
    this.user && (this.userData = R.clone(this.user))
  },
  watch: {
    user(current, previous) {
      if (!previous && current) {
        this.userData = R.clone(current)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/forms';
@import '~@/styles/styleConfigurations';
@import '~@/styles/errors';

$gutter-size: 12px;
.form {
  padding: $gutter-size;
}

/deep/ .invalid-feedback {
  padding-left: $gutter-size;
}

.form-error {
  color: $error;
  padding-left: $gutter-size;
}
@include media-breakpoint-up(md) {
  .form {
    width: 500px;
  }
}
</style>
